// The app's command set: thin, typed wrappers over the store actions and the
// pure music engine. Handlers read the store imperatively (getState) so they
// work headlessly — from the palette, from tests, and later from an AI tool
// loop — without any React context.

import { SWING_CHOICE_IDS, SWING_RATIO_MAX, SWING_RATIO_MIN, swingPatch } from '@/music/swingFeel';
import { z } from 'zod';
import { useStore, tuneArrangementOf, tuneSectionsOf } from '@/state/store';
import { sectionPlaysMelody } from '@/state/tunePlayback';
import { pickBand } from '@/state/persistence';
import { detectSections, printedBarNumber, sectionRows, writtenBarOf } from '@/music/sectionPlan';
import { pinCount } from '@/music/voicingPins';
import { describeEvent } from '@/music/score/voiceEdit';
import * as formEdit from '@/music/score/formEdit';
import { activeReharmLayer, dropReharmPreview, reharmLayers } from '@/music/score/reharm';
import { suggestForChord, type ReharmRung } from '@/music/reharm';
import { describeTidyReport } from '@/music/score/transcription';
import { getChordPlayer } from '@/audio/player';
import type { MixFader } from '@/audio/levels';
import { exportIRealURL } from '@/music/ireal/serialize';
import { pitchClassOf } from '@/music/notes';
import { CHORD_TYPE_IDS, type ChordTypeId } from '@/music/chords';
import { BASS_STYLES, type BassStyle } from '@/music/walkingBass';
import { COMP_STYLES, type CompStyle } from '@/music/comping';
import { PIANO_COMP_FAMILIES, type PianoCompFamily } from '@/music/pianoComping';
import { PIANO_LEFT_HAND_FAMILIES } from '@/music/pianoLeftHand';
import {
  BUNDLED_PLAYLISTS,
  fetchBundledPlaylist,
  playlistDisplayName,
} from '@/data/bundledPlaylists';
import { BUNDLED_SCORES, loadBundledScore } from '@/data/bundledScores';
import { loadOpenbookScore } from '@/data/openbookLibrary';
import { loadRealbookScore } from '@/data/realbookLibrary';
import { libraryTunes, loadLibraryScore } from '@/data/libraryTunes';
import { loadAccountTune } from '@/data/sharedTunes';
import type { PlaylistTuneRef } from '@/storage/playlists';
import { SONG_PRESETS } from '@/data/presets';
import { APP_ROUTES, isWorkbenchRoute, LEGACY_COMMAND_PAGES, PALETTE_PAGES } from '@/navigation';
import { defineCommand, registerCommands, type ErasedCommand } from './registry';
import { flagEnabled } from '@/auth/flags';
import { playlistsAvailable } from '@/data/playlistAccess';

export const PAGES = PALETTE_PAGES;
const COMMAND_PAGES = [...PAGES, ...LEGACY_COMMAND_PAGES] as const;
export type PageId = (typeof COMMAND_PAGES)[number]['id'];

const pageIds = COMMAND_PAGES.map((p) => p.id) as [PageId, ...PageId[]];
const chordTypeIds = CHORD_TYPE_IDS as [ChordTypeId, ...ChordTypeId[]];
const bassStyleIds = BASS_STYLES.map((s) => s.id) as [BassStyle, ...BassStyle[]];
const compStyleIds = COMP_STYLES.map((s) => s.id) as [CompStyle, ...CompStyle[]];
const pianoCompFamilyIds = [...PIANO_COMP_FAMILIES, ...PIANO_LEFT_HAND_FAMILIES].map(
  (f) => f.id,
) as [PianoCompFamily, ...PianoCompFamily[]];

/** A note name like C, F#, Bb — validated by the music engine's own parser. */
const noteName = z.string().refine(
  (s) => {
    try {
      pitchClassOf(s);
      return true;
    } catch {
      return false;
    }
  },
  { message: 'not a note name (expected e.g. C, F#, Bb)' },
);

/** The last ref kind load_tune reaches. Typed so that a kind added to the
 * union later fails to compile here instead of falling through to the
 * openbook loader, which is how realbook refs were being read. */
function openbookRef(ref: Extract<PlaylistTuneRef, { kind: 'openbook' }>) {
  return { id: ref.id, file: ref.file, title: ref.title };
}

/** Rank a candidate title against a query: prefix > word prefix > substring. */
function titleRank(title: string, q: string): number {
  const t = title.toLowerCase();
  if (t === q) return 0;
  if (t.startsWith(q)) return 1;
  if (t.split(/\s+/).some((w) => w.startsWith(q))) return 2;
  if (t.includes(q)) return 3;
  return Infinity;
}

const goToPage = defineCommand({
  name: 'go_to_page',
  title: 'Go to page',
  description: 'Navigate to one of the app pages.',
  schema: z.object({ page: z.enum(pageIds) }).strict(),
  run: ({ page }, ctx) => {
    const target = COMMAND_PAGES.find((p) => p.id === page)!;
    ctx.navigate(target.to);
    return { ok: true, message: `Opened ${target.label}.` };
  },
});

const openLibrary = defineCommand({
  name: 'open_library',
  title: 'Open bundled tune library',
  description:
    'Load one of the bundled playlists (e.g. "Chord charts", 1,460 jazz standards as iReal Pro charts) so its tunes become searchable and loadable by title.',
  schema: z.object({ name: z.string().optional() }).strict(),
  run: async ({ name }) => {
    const q = name?.trim().toLowerCase();
    // A list answers to its own name and to the one its file carries.
    const entry = q
      ? BUNDLED_PLAYLISTS.find((p) =>
          [p.name, p.fileName].some((known) => known.toLowerCase().includes(q)),
        )
      : BUNDLED_PLAYLISTS[0];
    if (!entry) return { ok: false, error: `No bundled library matching "${name}".` };
    const src = await fetchBundledPlaylist(entry);
    const res = useStore.getState().setPlaylist(src);
    if (!res.ok) return { ok: false, error: res.error };
    const count = useStore.getState().playlist?.songs.length ?? 0;
    return {
      ok: true,
      message: `Loaded ${entry.name} (${count} tunes).`,
      data: { name: entry.name, count },
    };
  },
});

const loadTune = defineCommand({
  name: 'load_tune',
  title: 'Load a tune by title',
  description:
    'Find a tune by (partial) title across the built-in presets, saved charts, the library, user playlists and the loaded iReal playlist, load the best match into the Workbench and jump there.',
  schema: z.object({ title: z.string().min(1) }).strict(),
  run: async ({ title }, ctx) => {
    const q = title.trim().toLowerCase();
    const s = useStore.getState();
    type Candidate = {
      title: string;
      source: string;
      rank: number;
      load: () => boolean | Promise<boolean>;
    };
    const candidates: Candidate[] = [];

    for (const preset of SONG_PRESETS) {
      candidates.push({
        title: preset.title,
        source: 'preset',
        rank: titleRank(preset.title, q),
        load: () => {
          useStore.getState().loadSong(preset);
          return true;
        },
      });
    }
    for (const saved of s.savedCharts) {
      candidates.push({
        title: saved.title,
        source: 'saved chart',
        rank: titleRank(saved.title, q),
        load: () => {
          useStore.getState().loadScore(saved);
          return true;
        },
      });
    }
    for (const entry of BUNDLED_SCORES) {
      candidates.push({
        title: entry.summary.title,
        source: 'native score',
        rank: titleRank(entry.summary.title, q),
        load: async () => {
          useStore.getState().loadScore(await loadBundledScore(entry));
          return true;
        },
      });
    }
    for (const entry of await libraryTunes()) {
      candidates.push({
        title: entry.title,
        source: 'library',
        rank: titleRank(entry.title, q),
        load: () =>
          loadLibraryScore(entry).then(
            (score) => {
              useStore.getState().loadScore(score);
              return true;
            },
            () => false,
          ),
      });
    }
    for (const list of playlistsAvailable() ? s.userPlaylists : []) {
      for (const { ref } of list.entries) {
        candidates.push({
          title: ref.title,
          source: list.name,
          rank: titleRank(ref.title, q),
          // Typed references (playlists v2): each kind loads its own way.
          load: () => {
            const state = useStore.getState();
            if (ref.kind === 'ireal')
              return state.loadTuneFromChunk(ref.scheme, ref.chunk) !== null;
            if (ref.kind === 'saved') {
              const saved = state.savedCharts.find((c) => c.id === ref.id);
              if (!saved) return false;
              state.loadScore(saved);
              return true;
            }
            if (ref.kind === 'account') {
              const own = state.savedCharts.find((c) => c.id === ref.id);
              if (own) {
                state.loadScore(own);
                return true;
              }
              return loadAccountTune(ref).then(
                (score) => {
                  useStore.getState().loadScore(score);
                  return true;
                },
                () => false,
              );
            }
            if (ref.kind === 'library') {
              return loadLibraryScore({ id: ref.id }).then(
                (score) => {
                  useStore.getState().loadScore(score);
                  return true;
                },
                () => false,
              );
            }
            if (ref.kind === 'score') {
              return loadBundledScore(ref.id, ref.revisionId).then(
                (score) => {
                  useStore.getState().loadScore(score);
                  return true;
                },
                () => false,
              );
            }
            // openbook and realbook refs share a shape, so realbook needs its
            // own branch: falling through fetched the file from openbook/ and
            // parsed MusicXML as LilyPond.
            const corpusScore =
              ref.kind === 'realbook'
                ? loadRealbookScore({ id: ref.id, file: ref.file })
                : loadOpenbookScore(openbookRef(ref));
            return corpusScore.then(
              (score) => {
                useStore.getState().loadScore(score);
                return true;
              },
              () => false,
            );
          },
        });
      }
    }
    if (s.playlist) {
      const scheme = s.playlist.scheme;
      const libName = playlistDisplayName(s.playlist.name) ?? 'library';
      for (const song of s.playlist.songs) {
        candidates.push({
          title: song.title,
          source: libName,
          rank: titleRank(song.title, q),
          load: () => useStore.getState().loadTuneFromChunk(scheme, song.chunk) !== null,
        });
      }
    }

    const best = candidates.filter((c) => c.rank !== Infinity).sort((a, b) => a.rank - b.rank)[0];
    if (!best) return { ok: false, error: `No tune matching "${title}".` };
    if (!(await best.load())) return { ok: false, error: `Could not read "${best.title}".` };
    ctx.navigate(APP_ROUTES.workbench);
    return {
      ok: true,
      message: `Loaded "${best.title}" (${best.source}).`,
      data: { title: best.title, source: best.source },
    };
  },
});

const newChart = defineCommand({
  name: 'new_chart',
  title: 'New tune',
  description:
    'Start a fresh empty chart (blank canvas), optionally with a title, composer, style and key.',
  schema: z
    .object({
      title: z.string().optional(),
      composer: z.string().optional(),
      style: z.string().optional(),
      key: z.string().optional(),
    })
    .strict(),
  run: (input, ctx) => {
    useStore.getState().newTune(input);
    ctx.navigate(APP_ROUTES.workbench);
    const chart = useStore.getState().chart;
    return { ok: true, message: `New tune "${chart.title}" in ${chart.key}.` };
  },
});

const setChartDetails = defineCommand({
  name: 'set_chart_details',
  title: 'Edit chart details',
  description: 'Update the current chart metadata: title, style label, and/or chorus repeat count.',
  schema: z
    .object({
      title: z.string().optional(),
      style: z.string().optional(),
      repeats: z.number().int().min(1).max(16).optional(),
    })
    .strict(),
  run: ({ title, style, repeats }) => {
    const s = useStore.getState();
    if (title !== undefined) s.setChartTitle(title);
    if (style !== undefined) s.setChartStyle(style);
    if (repeats !== undefined) s.setChartRepeats(repeats);
    return { ok: true, message: 'Chart details updated.' };
  },
});

const transpose = defineCommand({
  name: 'transpose',
  title: 'Transpose the chart',
  description:
    'Transpose the current chart, either by a number of semitones (positive = up) or to a target key tonic (e.g. "Bb"). Provide exactly one of the two.',
  schema: z
    .object({
      semitones: z.number().int().min(-11).max(11).optional(),
      toKey: noteName.optional(),
    })
    .strict()
    .refine((v) => (v.semitones !== undefined) !== (v.toKey !== undefined), {
      message: 'provide exactly one of semitones or toKey',
    }),
  run: ({ semitones, toKey }) => {
    const s = useStore.getState();
    if (toKey !== undefined) {
      s.transposeToKey(toKey);
      return { ok: true, message: `Transposed to ${useStore.getState().chart.key}.` };
    }
    s.transpose(semitones!);
    return {
      ok: true,
      message: `Transposed ${semitones! >= 0 ? 'up' : 'down'} ${Math.abs(semitones!)} semitones (now in ${useStore.getState().chart.key}).`,
    };
  },
});

const addChord = defineCommand({
  name: 'add_chord',
  title: 'Add a chord',
  description:
    'Append a chord to the current chart (bar overflow starts a new bar automatically). Optionally pin the voicing lead (top) note.',
  schema: z
    .object({
      root: noteName,
      chordType: z.enum(chordTypeIds),
      beats: z.number().int().min(1).max(8).optional(),
      targetTopNote: noteName.optional(),
    })
    .strict(),
  run: (input) => {
    useStore.getState().addChord(input);
    return { ok: true, message: `Added ${input.root} ${input.chordType}.` };
  },
});

const chordTypeEnum = z.enum(chordTypeIds);

/**
 * Why an edit to `chordId` cannot land, or null. The store runs harmony edits
 * beneath a live audition, so a chord only the audition wrote is not there to
 * edit: say so, rather than report an edit that never happened.
 */
function chordEditRefusal(
  chordId: string,
  missing = `No chord with id "${chordId}".`,
): string | null {
  const score = useStore.getState().score;
  if (formEdit.findChord(dropReharmPreview(score), chordId)) return null;
  return formEdit.findChord(score, chordId)
    ? 'That chord is part of the harmony preview. Keep the preview (reharm_audition keep) to edit it.'
    : missing;
}

const updateChord = defineCommand({
  name: 'update_chord',
  title: 'Update a chord',
  description:
    'Change a chord symbol by id: root, chord type, length in beats, slash bass or printed quality. ' +
    'Fields left out are unchanged. Chord ids come from the chart (chart.measures[].chords[].id).',
  schema: z
    .object({
      chordId: z.string().min(1),
      root: noteName.optional(),
      chordType: chordTypeEnum.optional(),
      beats: z.number().positive().max(8).optional(),
      bass: noteName.nullable().optional(),
      quality: z.string().max(12).optional(),
    })
    .strict()
    .refine((input) => Object.keys(input).some((key) => key !== 'chordId'), {
      message: 'provide at least one field to change',
    }),
  run: ({ chordId, bass, ...patch }) => {
    const refusal = chordEditRefusal(chordId);
    if (refusal) return { ok: false, error: refusal };
    useStore.getState().updateChord(chordId, {
      ...patch,
      ...(bass === undefined ? {} : { bass: bass ?? undefined }),
    });
    return { ok: true, message: 'Updated the chord.' };
  },
});

const removeChord = defineCommand({
  name: 'remove_chord',
  title: 'Remove a chord',
  description:
    'Remove a chord symbol from the chart by its id. The bar stays; its neighbours keep their beats.',
  schema: z.object({ chordId: z.string().min(1) }).strict(),
  run: ({ chordId }) => {
    const refusal = chordEditRefusal(chordId);
    if (refusal) return { ok: false, error: refusal };
    useStore.getState().removeChord(chordId);
    return { ok: true, message: 'Removed the chord.' };
  },
});

const replaceChord = defineCommand({
  name: 'replace_chord',
  title: 'Replace a chord with one or more',
  description:
    'Replace one chord by one or more chords occupying exactly its span — a substitution (one for one) ' +
    'or an insertion such as a ii–V for a V (two for one). Chords without beats share the span evenly; ' +
    'sized chords must fit inside it.',
  schema: z
    .object({
      chordId: z.string().min(1),
      chords: z
        .array(
          z
            .object({
              root: noteName,
              chordType: chordTypeEnum,
              beats: z.number().positive().max(8).optional(),
              bass: noteName.optional(),
            })
            .strict(),
        )
        .min(1)
        .max(8),
    })
    .strict(),
  run: ({ chordId, chords }) => {
    const refusal = chordEditRefusal(chordId);
    if (refusal) return { ok: false, error: refusal };
    const before = useStore.getState().score;
    useStore.getState().replaceChord(chordId, chords, { moveId: 'manual' });
    if (useStore.getState().score === before) {
      return { ok: false, error: "Those chords do not fit inside the replaced chord's span." };
    }
    return {
      ok: true,
      message: `Replaced it with ${chords.map((c) => `${c.root} ${c.chordType}`).join(', ')}.`,
    };
  },
});

const reharmLayer = defineCommand({
  name: 'reharm_layer',
  title: 'Manage reharmonization layers',
  description:
    'Reharm layers are named, swappable harmonizations of the loaded tune over its untouched original. ' +
    'create: new layer (from the Original, or derived from layer `from`) and make it active; ' +
    'activate: switch to a layer, or back to the Original with layer_id null; ' +
    'duplicate/rename/delete by layer_id; list: report the layers and which is active.',
  schema: z
    .object({
      action: z.enum(['create', 'activate', 'duplicate', 'rename', 'delete', 'list']),
      layer_id: z.string().nullable().optional(),
      name: z.string().max(60).optional(),
      from: z.string().optional(),
    })
    .strict(),
  run: ({ action, layer_id, name, from }) => {
    const state = useStore.getState();
    const layers = reharmLayers(state.score);
    // Read AFTER the action: every branch but list reports the store it left.
    const listing = () => {
      const now = useStore.getState().score;
      return {
        active: now.reharm?.active ?? null,
        layers: reharmLayers(now).map((l) => ({
          id: l.id,
          name: l.name,
          derivedFrom: l.derivedFrom,
          measures: Object.keys(l.overrides).length,
        })),
      };
    };
    const known = (id: string) => layers.some((l) => l.id === id);
    switch (action) {
      case 'list':
        return {
          ok: true,
          message:
            layers.length === 0
              ? 'No reharm layers; the tune plays its original harmony.'
              : `${layers.length} layer(s); active: ${
                  activeReharmLayer(state.score)?.name ?? 'Original'
                }.`,
          data: listing(),
        };
      case 'create': {
        if (from !== undefined && !known(from)) {
          return { ok: false, error: `No reharm layer with id "${from}".` };
        }
        const id = state.createReharmLayer({ name, from: from ?? null });
        return {
          ok: true,
          message: `Created and activated layer "${name?.trim() || activeReharmLayer(useStore.getState().score)?.name}".`,
          data: { layerId: id, ...listing() },
        };
      }
      case 'activate': {
        if (layer_id === undefined)
          return { ok: false, error: 'activate needs layer_id (null = Original).' };
        if (layer_id !== null && !known(layer_id)) {
          return { ok: false, error: `No reharm layer with id "${layer_id}".` };
        }
        state.activateReharmLayer(layer_id);
        return {
          ok: true,
          message: layer_id === null ? 'Back to the original harmony.' : 'Activated the layer.',
          data: listing(),
        };
      }
      case 'duplicate': {
        if (!layer_id || !known(layer_id)) {
          return { ok: false, error: `No reharm layer with id "${layer_id}".` };
        }
        const id = state.duplicateReharmLayer(layer_id, name);
        return {
          ok: true,
          message: 'Derived a new layer and activated it.',
          data: { layerId: id, ...listing() },
        };
      }
      case 'rename': {
        if (!layer_id || !known(layer_id)) {
          return { ok: false, error: `No reharm layer with id "${layer_id}".` };
        }
        if (!name?.trim()) return { ok: false, error: 'rename needs a name.' };
        state.renameReharmLayer(layer_id, name);
        return { ok: true, message: `Renamed the layer to "${name.trim()}".`, data: listing() };
      }
      case 'delete': {
        if (!layer_id || !known(layer_id)) {
          return { ok: false, error: `No reharm layer with id "${layer_id}".` };
        }
        state.deleteReharmLayer(layer_id);
        return { ok: true, message: 'Deleted the layer.', data: listing() };
      }
    }
  },
});

const reharmSuggest = defineCommand({
  name: 'reharm_suggest',
  title: 'Suggest reharmonizations for a chord',
  description:
    'List reharmonization candidates for one chord of the loaded tune, melody-checked and ranked: ' +
    'rung 0 colour (extensions), rung 1 diatonic substitutes, rung 2 dominant devices (tritone sub, ' +
    'ii–V insertion, secondary dominant, backdoor). Each candidate carries an id to pass to reharm_apply.',
  schema: z
    .object({
      chordId: z.string().min(1),
      max_rung: z.number().int().min(0).max(8).optional(),
    })
    .strict(),
  run: ({ chordId, max_rung }) => {
    if (!flagEnabled('reharm')) return REHARM_OFF;
    const refusal = chordEditRefusal(chordId);
    if (refusal) return { ok: false, error: refusal };
    // Worked out where reharm_apply will land them: beneath a live audition.
    const score = dropReharmPreview(useStore.getState().score);
    const candidates = suggestForChord(score, chordId, {
      maxRung: (max_rung ?? 8) as ReharmRung,
      voiceLeading: false,
    });
    return {
      ok: true,
      message:
        candidates.length === 0
          ? 'No reharmonization fits that chord under its melody.'
          : `${candidates.length} candidate(s): ${candidates
              .slice(0, 6)
              .map((c) => c.label)
              .join(', ')}${candidates.length > 6 ? '…' : ''}.`,
      data: {
        candidates: candidates.map((c) => ({
          id: c.id,
          move: c.moveId,
          rung: c.rung,
          label: c.label,
          rationale: c.rationale,
          melodyFit: c.score.melodyFit,
          functionKept: c.score.functionKept,
          changesOnsets: c.changesOnsets,
        })),
      },
    };
  },
});

// The reharm ENGINE is flag-gated (#327): pilot student profiles carry
// {"reharm": false} and every engine command answers the same refusal, so
// headless callers and the UI agree. Layer CRUD (reharm_layer) stays — the
// flag gates the engine, not the score data.
const REHARM_OFF = {
  ok: false as const,
  error: 'Reharmonisation is not available on this account.',
};

const reharmApply = defineCommand({
  name: 'reharm_apply',
  title: 'Apply a reharmonization candidate',
  description:
    'Apply a candidate from reharm_suggest by its id, into the active reharm layer (a new layer is ' +
    'created when the original is active, so the original is never overwritten).',
  schema: z.object({ candidateId: z.string().min(1) }).strict(),
  run: ({ candidateId }) => {
    if (!flagEnabled('reharm')) return REHARM_OFF;
    const [, chordId = ''] = candidateId.split(':');
    const state = useStore.getState();
    const refusal = chordEditRefusal(
      chordId,
      `Candidate "${candidateId}" does not name a chord in this tune.`,
    );
    if (refusal) return { ok: false, error: refusal };
    const target = dropReharmPreview(state.score);
    const candidate = suggestForChord(target, chordId, { voiceLeading: false }).find(
      (c) => c.id === candidateId,
    );
    if (!candidate) return { ok: false, error: `Candidate "${candidateId}" is not on offer.` };
    state.applyReharmCandidate(candidate);
    return {
      ok: true,
      message: `Applied ${candidate.label} (${candidate.moveId}) in layer "${
        activeReharmLayer(useStore.getState().score)?.name ?? 'Original'
      }".`,
    };
  },
});

const reharmPassage = defineCommand({
  name: 'reharm_passage',
  title: 'Reharmonize the selected bars as a new layer',
  description:
    'Derive a reharmonization of the selected passage (or the whole tune when no bars are selected) as a ' +
    'NEW layer: one melody-checked move per chosen chord, under a rung ceiling (0 colour … 5 bass line) and ' +
    'an adventure band (inside / stretched / out / free — how far from the corpus the changes may sit). ' +
    'density 0..1 is the share of chords touched.',
  schema: z
    .object({
      max_rung: z.number().int().min(0).max(8).optional(),
      adventure: z.enum(['inside', 'stretched', 'out', 'free']).optional(),
      density: z.number().min(0).max(1).optional(),
      variation: z.number().int().min(0).max(99).optional(),
      name: z.string().max(60).optional(),
    })
    .strict(),
  run: ({ max_rung, adventure, density, variation, name }) => {
    if (!flagEnabled('reharm')) return REHARM_OFF;
    const result = useStore.getState().deriveReharmPassage({
      maxRung: max_rung as ReharmRung | undefined,
      adventure,
      density,
      variation,
      name,
    });
    if (!result)
      return {
        ok: false,
        error: 'Nothing in those bars takes a reharmonization under that setting.',
      };
    const layer = activeReharmLayer(useStore.getState().score);
    return {
      ok: true,
      message: `Made layer "${layer?.name}" with ${result.count} change${result.count === 1 ? '' : 's'}.`,
      data: { layerId: result.layerId, count: result.count },
    };
  },
});

const reharmAudition = defineCommand({
  name: 'reharm_audition',
  title: 'Try a reharmonization before keeping it',
  description:
    'Audition a passage reharmonization: "try" applies it as a preview layer so it can be heard and seen ' +
    '(same settings as reharm_passage), "keep" turns it into an ordinary layer as one undo step, "cancel" ' +
    'puts the original harmony back. Re-trying replaces the audition rather than stacking on it, and any ' +
    'other edit to the music ends it.',
  schema: z
    .object({
      action: z.enum(['try', 'keep', 'cancel']),
      max_rung: z.number().int().min(0).max(8).optional(),
      adventure: z.enum(['inside', 'stretched', 'out', 'free']).optional(),
      density: z.number().min(0).max(1).optional(),
      variation: z.number().int().min(0).max(99).optional(),
      name: z.string().max(60).optional(),
    })
    .strict(),
  run: ({ action, max_rung, adventure, density, variation, name }) => {
    if (!flagEnabled('reharm')) return REHARM_OFF;
    const store = useStore.getState();
    if (action === 'cancel') {
      if (!store.reharmPreview) return { ok: false, error: 'Nothing is being auditioned.' };
      store.cancelReharmPreview();
      return { ok: true, message: 'Put the original harmony back.' };
    }
    if (action === 'keep') {
      const kept = store.keepReharmPreview(name);
      if (!kept) return { ok: false, error: 'Nothing is being auditioned.' };
      const layer = activeReharmLayer(useStore.getState().score);
      return {
        ok: true,
        message: `Kept as layer "${layer?.name}" with ${kept.count} change${kept.count === 1 ? '' : 's'}.`,
        data: { layerId: kept.layerId, count: kept.count },
      };
    }
    const count = store.previewReharmPassage({
      maxRung: max_rung as ReharmRung | undefined,
      adventure,
      density,
      variation,
    });
    if (count === 0)
      return {
        ok: false,
        error: 'Nothing in those bars takes a reharmonization under that setting.',
      };
    return {
      ok: true,
      message: `Playing ${count} new chord${count === 1 ? '' : 's'}. Keep or cancel.`,
      data: { count },
    };
  },
});

const importIReal = defineCommand({
  name: 'import_ireal',
  title: 'Import an iReal Pro link',
  description:
    'Parse an irealb:// or irealbook:// link (or share URL) and load its first song as the current chart.',
  schema: z.object({ text: z.string().min(1) }).strict(),
  run: ({ text }, ctx) => {
    const res = useStore.getState().importIRealText(text);
    if (!res.ok) return { ok: false, error: res.error };
    ctx.navigate(APP_ROUTES.workbench);
    return { ok: true, message: `Imported "${res.title}".`, data: { title: res.title } };
  },
});

const importAbc = defineCommand({
  name: 'import_abc',
  title: 'Import ABC',
  description:
    'Read ABC notation (abcnotation.com): one tune loads as the current score with its melody, chord symbols and lyrics; a tunebook of several tunes becomes the loaded tune list and its first tune loads. `name` names a tunebook that does not name itself.',
  schema: z.object({ text: z.string().min(1), name: z.string().optional() }).strict(),
  run: ({ text, name }, ctx) => {
    const res = useStore.getState().importAbc(text, name);
    if (!res.ok) return { ok: false, error: res.error };
    ctx.navigate(APP_ROUTES.workbench);
    const book = res.tunes > 1 ? ` (1 of ${res.tunes} tunes)` : '';
    return {
      ok: true,
      message: `Imported "${res.title}"${book}.`,
      data: { title: res.title, tunes: res.tunes },
    };
  },
});

const exportIReal = defineCommand({
  name: 'export_ireal',
  title: 'Export as iReal Pro link',
  description:
    'Serialize the current chart to an irealb:// URL that iReal Pro (and this app) can import.',
  schema: z.object({}).strict(),
  run: () => {
    const chart = useStore.getState().chart;
    const exported = exportIRealURL(chart);
    const warning = exported.warnings.length
      ? ` Warning: ${exported.warnings.length} Feel cue${exported.warnings.length === 1 ? '' : 's'} became visible staff text because iReal Pro cannot preserve typed Feel cues.`
      : '';
    return {
      ok: true,
      message: `Exported "${chart.title}".${warning}`,
      data: exported,
    };
  },
});

const saveChart = defineCommand({
  name: 'save_chart',
  title: 'Save the current chart',
  description: 'Save the current chart into the saved-charts list, optionally under a new title.',
  schema: z.object({ title: z.string().optional() }).strict(),
  run: ({ title }) => {
    const s = useStore.getState();
    const name = title?.trim() || s.chart.title || 'Untitled Chart';
    s.saveCurrentAs(name);
    return { ok: true, message: `Saved "${name}".` };
  },
});

const setTempo = defineCommand({
  name: 'set_tempo',
  title: 'Set tempo',
  description:
    'Set the playback tempo in BPM (clamped to 40–320). Applies live if something is playing.',
  schema: z.object({ bpm: z.number().min(40).max(320) }).strict(),
  run: ({ bpm }) => {
    useStore.getState().setTempo(bpm);
    return { ok: true, message: `Tempo ${useStore.getState().tempo} BPM.` };
  },
});

/** A swing offset, as a fraction of the beat: anything past the whole range,
 *  straight to 7:1, in either direction is refused. */
const SWING_OFFSET_SPAN = SWING_RATIO_MAX - SWING_RATIO_MIN;
const SWING_OFFSET_SCHEMA = z.number().min(-SWING_OFFSET_SPAN).max(SWING_OFFSET_SPAN);

const setPlayback = defineCommand({
  name: 'set_playback',
  title: 'Playback options',
  description:
    "Set playback options: metronome click, one-bar count-in, looping the form, the drum loop (on/off and which loop by manifest key), the master effects (compression then reverb amounts 0..1; *_enabled bypasses an effect without changing its amount; compression booleans also toggle its remembered amount, default 0.5), the mixer levels (melody/comp/bass/drums 0..1.5 with 1 = unity, master 0..1), the ending (ritardando bars, tag bars and times, hold the last chord), and the feel (swing: the triplet feel for this tune — straight, kit (the default: the eighths swing where the ride in the drum loop plays its skip note, measured from the loop; the triplet when there is no kit or no clear skip note), or the eighth or sixteenth note that swings with a fixed ratio — triplet 2:1, dotted 3:1, scottish 1:3 short-long; a passage finer than the swung note stays straight; swing_offset: under kit, how far past the kit's own swing the band plays its eighth \"and\", as a fraction of the beat added to the kit's ratio — 0 is the kit, 0.03 a little harder, -0.05 a little straighter, held between straight and 7:1; set_melody, set_comping and set_bass move each part past the band the same way, and the drums keep the loop's own swing; feel: how loose the band is — tight is exact placement, relaxed and loose add a slow drift of a few milliseconds and a few percent of level; the placement of each part against the kit is set with set_bass, set_comping and set_melody).",
  schema: z
    .object({
      metronome: z.boolean().optional(),
      swing: z.enum(SWING_CHOICE_IDS).optional(),
      swing_offset: SWING_OFFSET_SCHEMA.optional(),
      feel: z.enum(['tight', 'relaxed', 'loose']).optional(),
      countIn: z.boolean().optional(),
      repeatForm: z.boolean().optional(),
      drums: z.boolean().optional(),
      drumLoopKey: z.string().optional(),
      reverb: z.number().min(0).max(1).optional(),
      reverb_enabled: z.boolean().optional(),
      compression: z.union([z.number().min(0).max(1), z.boolean()]).optional(),
      compression_enabled: z.boolean().optional(),
      /** Fader positions: 1 is the calibrated balance; channels may boost to 1.5, the master only attenuates. */
      levels: z
        .object({
          melody: z.number().min(0).max(1.5).optional(),
          comp: z.number().min(0).max(1.5).optional(),
          bass: z.number().min(0).max(1.5).optional(),
          drums: z.number().min(0).max(1.5).optional(),
          click: z.number().min(0).max(1.5).optional(),
          master: z.number().min(0).max(1).optional(),
        })
        .strict()
        .optional(),
      /** The metronome: its voice, which beats click, the fill between them, and whether it goes through the master effects. */
      click: z
        .object({
          sound: z.enum(['beep', 'woodblock', 'rim', 'cowbell', 'hihat']).optional(),
          accents: z.enum(['all', 'two-four', 'one']).optional(),
          subdivision: z.enum(['none', 'eighths', 'swing', 'triplets', 'sixteenths']).optional(),
          routing: z.enum(['pre', 'post']).optional(),
        })
        .strict()
        .optional(),
      /** How the last pass finishes: slow down over N bars, tag the last N bars M times, hold the final chord, resolve to the key chord. */
      ending: z
        .object({
          ritardando_bars: z
            .union([z.literal(0), z.literal(1), z.literal(2), z.literal(4)])
            .optional(),
          tag_bars: z.union([z.literal(0), z.literal(2), z.literal(4)]).optional(),
          tag_times: z.union([z.literal(2), z.literal(3)]).optional(),
          hold_last: z.boolean().optional(),
          /** Add a bar on the key chord after an unresolved last bar: home, through ♭VI∆ or ♭II∆, or stop on bar 1's chord. */
          resolve: z.enum(['off', 'home', 'flat-six', 'flat-two', 'top']).optional(),
        })
        .strict()
        .optional(),
    })
    .strict(),
  run: (input) => {
    const s = useStore.getState();
    // Transport options are global session state; the DRUM fields are band
    // state, so they go through patchBand and are remembered per tune (#123).
    if (input.metronome !== undefined) s.setMetronome(input.metronome);
    if (input.countIn !== undefined) s.setCountIn(input.countIn);
    if (input.repeatForm !== undefined) s.setRepeatForm(input.repeatForm);
    if (input.reverb !== undefined) s.setMasterReverb(input.reverb);
    if (input.compression !== undefined) s.setMasterCompression(input.compression);
    if (input.reverb_enabled !== undefined) s.setMasterReverbEnabled(input.reverb_enabled);
    if (input.compression_enabled !== undefined)
      s.setMasterCompressionEnabled(input.compression_enabled);
    for (const [fader, level] of Object.entries(input.levels ?? {})) {
      if (level !== undefined) s.setChannelLevel(fader as MixFader, level);
    }
    if (input.click) s.patchMetronome(input.click);
    // The swing feel is band state, remembered per tune like the drums.
    if (input.swing !== undefined) s.patchBand(swingPatch(input.swing));
    if (input.swing_offset !== undefined) s.patchBand({ swingOffset: input.swing_offset });
    if (input.feel !== undefined) s.setFeelTiming(input.feel);
    if (input.ending) {
      const e = input.ending;
      s.patchEnding({
        ...(e.ritardando_bars !== undefined ? { ritardandoBars: e.ritardando_bars } : {}),
        ...(e.tag_bars !== undefined ? { tagBars: e.tag_bars } : {}),
        ...(e.tag_times !== undefined ? { tagTimes: e.tag_times } : {}),
        ...(e.hold_last !== undefined ? { holdLast: e.hold_last } : {}),
        ...(e.resolve !== undefined ? { resolve: e.resolve } : {}),
      });
    }
    if (input.drums !== undefined || input.drumLoopKey !== undefined) {
      s.patchBand({
        ...(input.drums !== undefined ? { drums: input.drums } : {}),
        ...(input.drumLoopKey !== undefined ? { drumLoopKey: input.drumLoopKey } : {}),
      });
    }
    return { ok: true, message: 'Playback options updated.' };
  },
});

const setPractice = defineCommand({
  name: 'set_practice',
  title: 'Practice options',
  description:
    'Configure practice playback: speed as a percent of the arrangement tempo, the progressive speed trainer (from/to/step percent, passes per step — steps the tempo each form pass), count-in length in bars, and count-in between loops.',
  schema: z
    .object({
      speed_pct: z.number().min(25).max(150).optional(),
      trainer_enabled: z.boolean().optional(),
      trainer_from_pct: z.number().min(25).max(150).optional(),
      trainer_to_pct: z.number().min(25).max(150).optional(),
      trainer_step_pct: z.number().min(1).max(50).optional(),
      trainer_passes_per_step: z.number().int().min(1).max(8).optional(),
      count_in_bars: z.number().int().min(1).max(4).optional(),
      count_in_between_loops: z.boolean().optional(),
    })
    .strict(),
  run: (input) => {
    const s = useStore.getState();
    if (input.speed_pct !== undefined) s.setSpeedPct(input.speed_pct);
    const trainerPatch: Record<string, number | boolean> = {};
    if (input.trainer_enabled !== undefined) trainerPatch.enabled = input.trainer_enabled;
    if (input.trainer_from_pct !== undefined) trainerPatch.fromPct = input.trainer_from_pct;
    if (input.trainer_to_pct !== undefined) trainerPatch.toPct = input.trainer_to_pct;
    if (input.trainer_step_pct !== undefined) trainerPatch.stepPct = input.trainer_step_pct;
    if (input.trainer_passes_per_step !== undefined)
      trainerPatch.passesPerStep = input.trainer_passes_per_step;
    if (Object.keys(trainerPatch).length > 0) s.patchTrainer(trainerPatch);
    if (input.count_in_bars !== undefined) s.setCountInBars(input.count_in_bars);
    if (input.count_in_between_loops !== undefined)
      s.setCountInBetweenLoops(input.count_in_between_loops);
    return { ok: true, message: 'Practice options updated.' };
  },
});

const setBass = defineCommand({
  name: 'set_bass',
  title: 'Bass line options',
  description:
    'Configure the generated bass line: on/off, solo, style (walking ladder, boogie shuffle, bossa/latin), two- vs four-feel, swing, embellishments (ghost notes, anticipations, triplet drops, skips — the eighth before a beat plucking the note again or the target early) and their density, where it sits against the kit (placement: on-top 8 ms ahead, centre 2 ms ahead, behind 6 ms late), which beats it leans on (accent: even, two-four, one-three) and, under the kit swing, how far past the band\'s swing it plays its eighth "and" (swing_offset, a fraction of the beat). follow_form automates the convention under the head/solos/head policy: heads in 2, solo choruses walking in 4.',
  schema: z
    .object({
      enabled: z.boolean().optional(),
      solo: z.boolean().optional(),
      style: z.enum(bassStyleIds).optional(),
      feel: z.enum(['two', 'four']).optional(),
      follow_form: z.boolean().optional(),
      swing: z.boolean().optional(),
      ghosts: z.boolean().optional(),
      anticipate: z.boolean().optional(),
      triplets: z.boolean().optional(),
      skips: z.boolean().optional(),
      amount: z.number().min(0).max(1).optional(),
      placement: z.enum(['on-top', 'centre', 'behind']).optional(),
      accent: z.enum(['even', 'two-four', 'one-three']).optional(),
      swing_offset: SWING_OFFSET_SCHEMA.optional(),
    })
    .strict(),
  // Through patchBand — the SAME domain mutation the mixer uses — so a
  // command/AI edit is remembered for this tune instead of leaking globally
  // and vanishing on reload (#123).
  run: (input) => {
    useStore.getState().patchBand({
      ...(input.enabled !== undefined ? { bassline: input.enabled } : {}),
      ...(input.solo !== undefined ? { bassSolo: input.solo } : {}),
      ...(input.style !== undefined ? { bassStyle: input.style } : {}),
      ...(input.feel !== undefined ? { bassFeel: input.feel } : {}),
      ...(input.follow_form !== undefined ? { bassFollowForm: input.follow_form } : {}),
      ...(input.swing !== undefined ? { bassSwing: input.swing } : {}),
      ...(input.ghosts !== undefined ? { bassGhosts: input.ghosts } : {}),
      ...(input.anticipate !== undefined ? { bassAnticipate: input.anticipate } : {}),
      ...(input.triplets !== undefined ? { bassTriplets: input.triplets } : {}),
      ...(input.skips !== undefined ? { bassSkips: input.skips } : {}),
      ...(input.amount !== undefined ? { bassAmount: input.amount } : {}),
      ...(input.placement !== undefined ? { bassPlacement: input.placement } : {}),
      ...(input.accent !== undefined ? { bassAccent: input.accent } : {}),
      ...(input.swing_offset !== undefined ? { bassSwingOffset: input.swing_offset } : {}),
    });
    return { ok: true, message: 'Bass options updated.' };
  },
});

const setComping = defineCommand({
  name: 'set_comping',
  title: 'Comping options',
  description:
    "Set the comping instrument ('guitar' grips, 'piano' with both hands, or 'piano-left' — the " +
    'left hand alone under the melody in the right hand, the trio texture), the piano voicing ' +
    'family (two-hand, upper-structure triads, rootless…; lh-rootless, lh-shell, lh-root-seventh, ' +
    'lh-close for the left hand), the rhythm style ' +
    "('interactive' answers the written melody with varied jazz figures), " +
    'the activity amount (freestyle stab density / interactive busyness), and where the comp ' +
    'sits against the kit (placement: on-top 4 ms ahead, centre 4 ms late, behind 10 ms late), ' +
    'and under the kit swing how far past the band\'s swing it plays its eighth "and" ' +
    '(swing_offset, a fraction of the beat).',
  schema: z
    .object({
      instrument: z.enum(['guitar', 'piano', 'piano-left']).optional(),
      piano_family: z.enum(pianoCompFamilyIds).optional(),
      style: z.enum(compStyleIds).optional(),
      amount: z.number().min(0).max(1).optional(),
      placement: z.enum(['on-top', 'centre', 'behind']).optional(),
      swing_offset: SWING_OFFSET_SCHEMA.optional(),
    })
    .strict(),
  // Through patchBand, like the mixer — see set_bass above (#123).
  run: (input) => {
    useStore.getState().patchBand({
      ...(input.instrument !== undefined ? { compInstrument: input.instrument } : {}),
      ...(input.piano_family !== undefined ? { compPianoFamily: input.piano_family } : {}),
      ...(input.style !== undefined ? { compStyle: input.style } : {}),
      ...(input.amount !== undefined ? { compAmount: input.amount } : {}),
      ...(input.placement !== undefined ? { compPlacement: input.placement } : {}),
      ...(input.swing_offset !== undefined ? { compSwingOffset: input.swing_offset } : {}),
    });
    return { ok: true, message: 'Comping options updated.' };
  },
});

const setMelody = defineCommand({
  name: 'set_melody',
  title: 'Melody options',
  description:
    "How the band plays the tune's written melody: on/off, where its swung downbeats sit " +
    'against the kit (placement: on-top on the beat, centre 15 ms late, behind 30 ms late at ' +
    'medium tempos and less when faster; the offbeats stay on the ride either way), and its ' +
    "phrasing ('swing': legato downbeats, detached and accented upbeats, phrase ends clipped, " +
    "where the band swings; 'written': as written), and under the kit swing how far past the " +
    'band\'s swing it plays its eighth "and" (swing_offset, a fraction of the beat).',
  schema: z
    .object({
      enabled: z.boolean().optional(),
      placement: z.enum(['on-top', 'centre', 'behind']).optional(),
      phrasing: z.enum(['written', 'swing']).optional(),
      swing_offset: SWING_OFFSET_SCHEMA.optional(),
    })
    .strict(),
  // Placement and phrasing are band fields, through patchBand like the mixer.
  run: (input) => {
    const s = useStore.getState();
    if (input.enabled !== undefined) s.setMelodyChannel(input.enabled);
    if (
      input.placement !== undefined ||
      input.phrasing !== undefined ||
      input.swing_offset !== undefined
    ) {
      s.patchBand({
        ...(input.placement !== undefined ? { melodyPlacement: input.placement } : {}),
        ...(input.phrasing !== undefined ? { melodyPhrasing: input.phrasing } : {}),
        ...(input.swing_offset !== undefined ? { melodySwingOffset: input.swing_offset } : {}),
      });
    }
    return { ok: true, message: 'Melody options updated.' };
  },
});

const setSections = defineCommand({
  name: 'set_sections',
  title: 'Sections',
  description:
    "Play the tune's sections (A, B, Intro…) with a band each, as a playback choice: nothing " +
    "is written into the score. Sections come from the chart's own letters, or are guessed from " +
    'the changes when it has none. `on` turns playing by section on or off. `starts` replaces ' +
    'the sections (bar numbers as printed, from 1) and `starts: null` returns to the detected ' +
    'ones. `assign` copies the band playing now onto a section, its comp instrument and piano ' +
    'voicings included (a trio comping the A sections with the left hand under the melody and ' +
    'the bridge with both hands); `clear` takes a section back to the band playing now. ' +
    '`melody` sets whether a section plays the written melody. Returns the sections in force.',
  schema: z
    .object({
      on: z.boolean().optional(),
      starts: z
        .array(
          z
            .object({ bar: z.number().int().min(1), label: z.string().trim().min(1).max(20) })
            .strict(),
        )
        .min(1)
        .max(64)
        .nullable()
        .optional(),
      assign: z.string().trim().min(1).max(20).optional(),
      clear: z.string().trim().min(1).max(20).optional(),
      melody: z
        .object({ section: z.string().trim().min(1).max(20), on: z.boolean() })
        .strict()
        .optional(),
    })
    .strict(),
  run: (input) => {
    const store = useStore.getState();
    const measures = store.chart.measures;
    if (!measures.length) return { ok: false, error: 'This tune has no bars yet.' };
    if (input.starts !== undefined) {
      store.setSectionStarts(
        input.starts === null
          ? null
          : input.starts.map((start) => ({
              bar: writtenBarOf(measures, start.bar),
              label: start.label,
            })),
      );
    }
    if (input.assign) store.assignSectionBand(input.assign, pickBand(useStore.getState()));
    if (input.clear) store.assignSectionBand(input.clear);
    if (input.melody) store.setSectionMelody(input.melody.section, input.melody.on);
    if (input.on !== undefined) store.setSectionsOn(input.on);
    const s = useStore.getState();
    const sections = tuneSectionsOf(s);
    const starts = sections.starts ?? detectSections(s.chart)?.starts ?? [];
    const rows = sectionRows(starts, s.chart.measures.length).map((row) => ({
      label: row.label,
      bars: row.ranges.map(([from, to]) => [
        printedBarNumber(s.chart.measures, from),
        printedBarNumber(s.chart.measures, to),
      ]),
      assigned: !!sections.bands?.[row.label],
      plays: (sections.bands?.[row.label] ?? pickBand(s)).compInstrument,
      melody: sectionPlaysMelody(sections, row.label),
    }));
    return {
      ok: true,
      message: sections.on ? 'Playing by section.' : 'Sections are off.',
      data: { on: !!sections.on, sections: rows },
    };
  },
});

const setView = defineCommand({
  name: 'set_view',
  title: 'Lead-sheet view',
  description:
    'Choose what the Workbench shows. The view is a reading surface (the chord chart, the ' +
    'notation score, or the tune map: the chords as cadence cells relative to the home key) ' +
    'plus a voicings lens riding on it: none, comping (voice-led backing ' +
    'grips) or melody (the chord-melody arrangement; needs a tune with a melody). `pages` lays ' +
    'the chart-surface sheets out as side-by-side pages that fit a wide desktop window. ' +
    '`chords` picks which chord diagrams the voicings sheet draws — guitar grips or piano ' +
    'keyboards — a display choice, independent of which instrument the comp channel plays. ' +
    '`transposition` picks the reading edition: `c` (concert pitch, as written), `bb`, `eb` or ' +
    "`c-bass` — the sheet's chord symbols, key, melody and clef move for a B♭, E♭ or bass-clef " +
    'player while the band keeps playing in concert pitch. `bars_per_line` sets how many full ' +
    'bars a line of the score view holds (2, 3, 4, 6 or 8; `auto` fills the width): a pickup ' +
    'is not counted, sections, later endings and codas start new lines, and the staff draws ' +
    'smaller so the lines fit, splitting a line in half past a size floor. The score studio ' +
    'and the voicing lenses keep filling the width.',
  schema: z
    .object({
      surface: z.enum(['chart', 'score', 'map']).optional(),
      voicings: z.enum(['none', 'comping', 'melody']).optional(),
      pages: z.boolean().optional(),
      chords: z.enum(['guitar', 'piano']).optional(),
      transposition: z.enum(['c', 'bb', 'eb', 'c-bass']).optional(),
      bars_per_line: z
        .union([
          z.literal('auto'),
          z.literal(2),
          z.literal(3),
          z.literal(4),
          z.literal(6),
          z.literal(8),
        ])
        .optional(),
    })
    .strict()
    .refine(
      (input) =>
        input.surface !== undefined ||
        input.voicings !== undefined ||
        input.pages !== undefined ||
        input.chords !== undefined ||
        input.transposition !== undefined ||
        input.bars_per_line !== undefined,
      {
        message:
          'Provide a surface, a voicings lens, page view, chord diagrams, a reading transposition, bars per line, or a combination.',
      },
    ),
  run: ({ surface, voicings, pages, chords, transposition, bars_per_line }) => {
    const s = useStore.getState();
    if (surface) s.setViewSurface(surface);
    if (voicings) s.setVoicingLens(voicings);
    if (pages !== undefined) s.setSheetPages(pages);
    if (chords) s.setDiagramInstrument(chords);
    if (transposition) s.setScoreEdition(transposition);
    if (bars_per_line !== undefined) s.setBarsPerLine(bars_per_line);
    const now = useStore.getState();
    return {
      ok: true,
      message: `View: ${now.viewSurface} · voicings ${now.voicingLens} · pages ${
        now.sheetPages ? 'on' : 'off'
      } · chords ${now.diagramInstrument} · reading ${now.scoreEdition} · bars per line ${
        now.barsPerLine
      }.`,
    };
  },
});

const toggleTheme = defineCommand({
  name: 'toggle_theme',
  title: 'Toggle colour theme',
  description: 'Switch between the light and dark colour themes.',
  schema: z.object({}).strict(),
  run: () => {
    useStore.getState().toggleTheme();
    return { ok: true, message: `Theme: ${useStore.getState().theme}.` };
  },
});

const togglePageView = defineCommand({
  name: 'toggle_page_view',
  title: 'Toggle page view',
  description:
    'Switch the Workbench sheets between the classic vertical strip and side-by-side pages ' +
    'that fit the screen (wide desktop windows). Every workspace cell paginates — both ' +
    'reading surfaces and both voicing lenses — so this never has to change the view to ' +
    'take effect.',
  schema: z.object({}).strict(),
  run: (_input, ctx) => {
    const s = useStore.getState();
    const on = !s.sheetPages;
    s.setSheetPages(on);
    if (on && typeof window !== 'undefined' && !isWorkbenchRoute(window.location.pathname)) {
      // The setting only draws on the workspace, so show it doing something.
      ctx.navigate(APP_ROUTES.workbench);
    }
    return {
      ok: true,
      message: on
        ? 'Page view on — side-by-side pages; widen the window for more pages.'
        : 'Page view off — back to the vertical sheet.',
    };
  },
});

const voicingPins = defineCommand({
  name: 'voicing_pins',
  title: 'Voicing pins',
  description:
    "List or reset the loaded tune's pinned voicings — the user's own grips laid over the " +
    'generated line, by lane (comping or melody plan × guitar or piano). Pins are made from ' +
    "a grip's inspector on the sheet; this command reads or clears them.",
  schema: z
    .object({
      action: z.enum(['list', 'clear']),
      lane: z.enum(['comping.guitar', 'comping.piano', 'melody.guitar', 'melody.piano']).optional(),
    })
    .strict(),
  run: ({ action, lane }) => {
    const s = useStore.getState();
    const pins = tuneArrangementOf(s)?.pins;
    const count = pinCount(pins, lane);
    if (action === 'list') {
      return {
        ok: true,
        message: `${count} pinned voicing${count === 1 ? '' : 's'}${lane ? ` in ${lane}` : ''}`,
        data: lane ? (pins?.[lane] ?? {}) : (pins ?? {}),
      };
    }
    s.clearVoicingPins(lane);
    return {
      ok: true,
      message: count === 0 ? 'Nothing pinned' : lane ? `Pins reset in ${lane}` : 'All pins reset',
    };
  },
});

/** The tune a taste command names: the one given, or the one on screen. */
function tasteTuneOf(title: string | undefined, composer: string | undefined) {
  const s = useStore.getState();
  if (title?.trim()) {
    return { title: title.trim(), ...(composer?.trim() ? { composer: composer.trim() } : {}) };
  }
  const loaded = s.score.title.trim();
  if (!loaded || loaded === 'Untitled Chart') return null;
  const by = s.score.composer?.trim();
  return {
    title: loaded,
    ...(by ? { composer: by } : {}),
    ...(s.currentTuneRef ? { ref: s.currentTuneRef } : {}),
  };
}

const tuneTaste = defineCommand({
  name: 'tune_taste',
  title: 'Love a tune',
  description:
    'Love a tune, or clear its mark; or list the loved tunes. A loved tune counts toward ' +
    "Top picks and joins the home page's Loved shelf. Names the loaded tune unless a title " +
    'is given (a composer tells apart two tunes sharing a title).',
  schema: z
    .object({
      action: z.enum(['love', 'clear', 'list']),
      title: z.string().min(1).optional(),
      composer: z.string().min(1).optional(),
    })
    .strict(),
  run: ({ action, title, composer }) => {
    const s = useStore.getState();
    if (action === 'list') {
      const loved = s.tuneTaste
        .filter((mark) => mark.verdict === 'love')
        .map((mark) => ({
          title: mark.title,
          ...(mark.composer ? { composer: mark.composer } : {}),
        }));
      return { ok: true, message: `${loved.length} loved`, data: { loved } };
    }
    const tune = tasteTuneOf(title, composer);
    if (!tune) return { ok: false, error: 'No tune is loaded. Name one with title.' };
    s.setTuneTaste(tune, action === 'clear' ? null : action);
    return {
      ok: true,
      message: action === 'love' ? `Loved ${tune.title}` : `Cleared the mark on ${tune.title}`,
    };
  },
});

/** Which pages carry a sheet worth printing: the workbench (either surface,
 * any lens) and the Score Studio. Anywhere else `window.print()` would print
 * the app's own chrome — a home page, a playlist — which is never what
 * "print" meant, so the command refuses rather than guessing. */
function hasPrintableSheet(pathname: string): boolean {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return isWorkbenchRoute(normalized) || normalized === APP_ROUTES.scoreStudio;
}

const printSheet = defineCommand({
  name: 'print_sheet',
  title: 'Print',
  description:
    'Open the browser print dialog for the sheet on screen — the workbench chart or score, ' +
    'or the Score Studio — which is also how a phone saves it as a PDF. The paper carries ' +
    'the masthead and the music alone (no app chrome), one page break per system or chart ' +
    'row. Refuses on pages with no sheet.',
  schema: z.object({}).strict(),
  run: () => {
    if (typeof window === 'undefined' || typeof window.print !== 'function') {
      return { ok: false, error: 'Printing is not available here.' };
    }
    if (!hasPrintableSheet(window.location.pathname)) {
      return { ok: false, error: 'Open a tune to print it.' };
    }
    window.print();
    return { ok: true, message: 'Print dialog opened.' };
  },
});

const pausePlayback = defineCommand({
  name: 'pause_playback',
  title: 'Pause playback',
  description: 'Pause the running arrangement at the next beat (no-op if nothing is playing).',
  schema: z.object({}).strict(),
  run: () => {
    getChordPlayer().pause();
    return { ok: true, message: 'Paused.' };
  },
});

const resumePlayback = defineCommand({
  name: 'resume_playback',
  title: 'Resume playback',
  description: 'Resume a paused arrangement from where it stopped (no-op if not paused).',
  schema: z.object({}).strict(),
  run: () => {
    getChordPlayer().resumePlayback();
    return { ok: true, message: 'Resumed.' };
  },
});

const stopPlayback = defineCommand({
  name: 'stop_playback',
  title: 'Stop playback',
  description: 'Stop playback and reset the playhead to the start.',
  schema: z.object({}).strict(),
  run: () => {
    getChordPlayer().stop();
    return { ok: true, message: 'Stopped.' };
  },
});

const createPlaylist = defineCommand({
  name: 'create_playlist',
  title: 'Create a playlist',
  description: 'Create a new, empty user playlist with the given name.',
  schema: z.object({ name: z.string().min(1) }).strict(),
  run: ({ name }) => {
    // Playlists live in your account (src/data/playlistAccess.ts).
    if (!playlistsAvailable()) {
      return { ok: false, error: 'Sign in to make playlists.' };
    }
    const id = useStore.getState().createPlaylist(name);
    return { ok: true, message: `Created playlist "${name}".`, data: { id } };
  },
});

// --- score editor (melody) commands — migration slice 3: editing reaches the
// score model through the same typed layer the AI integration will use. Beats
// are 1-based within the measure, in the meter's denominator unit; durations
// are in the same unit (1 = one beat; 0.5 = half a beat).

const scoreAddNote = defineCommand({
  name: 'score_add_note',
  title: 'Add a melody note',
  description:
    'Add a melody note (or rest) to a measure of the loaded chart, for the Score page. ' +
    'measureIndex is 0-based; beat is 1-based within the measure; duration is in beats. ' +
    'voice 0 (default) is the melody, voice 1 the stems-down second voice. ' +
    'Returns the new note id.',
  schema: z
    .object({
      measureIndex: z.number().int().min(0),
      midi: z.number().int().min(0).max(127),
      beat: z.number().min(1),
      duration: z.number().positive().max(16),
      rest: z.boolean().optional(),
      voice: z.number().int().min(0).max(1).optional(),
    })
    .strict(),
  run: ({ measureIndex, midi, beat, duration, rest, voice }) => {
    const s = useStore.getState();
    if (!s.chart.measures[measureIndex]) {
      return { ok: false, error: `No measure at index ${measureIndex}.` };
    }
    // Diff the SCORE's voice events (voice 1 never projects into the chart).
    const eventIds = (state: typeof s) => {
      const items = state.score.parts[0]?.measures[measureIndex]?.voices[voice ?? 0]?.items ?? [];
      return items
        .flatMap((it) => ('kind' in it && it.kind === 'tuplet' ? it.items : [it]))
        .map((e) => e.id);
    };
    const before = eventIds(s);
    const beforeScore = s.score;
    s.scoreAddNote(
      measureIndex,
      { midi, beat, duration, ...(rest ? { rest: true } : {}) },
      voice ?? 0,
    );
    const next = useStore.getState();
    if (next.score === beforeScore) {
      return {
        ok: false,
        error: 'That placement is not supported inside or alongside this tuplet.',
      };
    }
    const after = eventIds(next);
    const added = after.find((id) => !before.includes(id) && !id.startsWith('r:'));
    const addedNote = added ? { id: added } : undefined;
    return {
      ok: true,
      message: `Added ${rest ? 'a rest' : `note ${midi}`} to measure ${measureIndex + 1}${voice === 1 ? ' (voice 2)' : ''}.`,
      data: { noteId: addedNote?.id },
    };
  },
});

const scoreAppendNote = defineCommand({
  name: 'score_append_note',
  title: 'Append a melody note',
  description:
    'Append a pitch after the last melody note of the loaded chart (piano-roll entry): fills ' +
    'the last measure or starts a new one. Duration is in beats; defaults to the editor entry duration.',
  schema: z
    .object({
      midi: z.number().int().min(0).max(127),
      duration: z.number().positive().max(16).optional(),
    })
    .strict(),
  run: ({ midi, duration }) => {
    const beforeScore = useStore.getState().score;
    useStore.getState().scoreAppendNote(midi, duration);
    if (useStore.getState().score === beforeScore) {
      return {
        ok: false,
        error: 'Appending is not supported while the final voice contains this tuplet.',
      };
    }
    return { ok: true, message: `Appended note ${midi}.` };
  },
});

const scoreUpdateNote = defineCommand({
  name: 'score_update_note',
  title: 'Update a melody note',
  description:
    'Update a melody note by id: move it (beat), change its length (duration, in beats) or ' +
    'pitch (midi). Fields left out are unchanged.',
  schema: z
    .object({
      noteId: z.string().min(1),
      midi: z.number().int().min(0).max(127).optional(),
      beat: z.number().min(1).optional(),
      duration: z.number().positive().max(16).optional(),
    })
    .strict()
    .refine(
      ({ midi, beat, duration }) =>
        midi !== undefined || beat !== undefined || duration !== undefined,
      { message: 'provide at least one of midi, beat, or duration' },
    ),
  run: ({ noteId, ...patch }) => {
    const beforeScore = useStore.getState().score;
    if (!describeEvent(beforeScore, noteId)) {
      return { ok: false, error: `No melody note with id "${noteId}".` };
    }
    useStore.getState().scoreUpdateNote(noteId, patch);
    if (useStore.getState().score === beforeScore) {
      return {
        ok: false,
        error: 'That timing edit is not supported for this tuplet.',
      };
    }
    return { ok: true, message: 'Updated the note.' };
  },
});

const scoreDeleteNote = defineCommand({
  name: 'score_delete_note',
  title: 'Delete a melody note',
  description: 'Delete a melody note (or rest) from the loaded chart by its note id.',
  schema: z.object({ noteId: z.string().min(1) }).strict(),
  run: ({ noteId }) => {
    const beforeScore = useStore.getState().score;
    if (!describeEvent(beforeScore, noteId)) {
      return { ok: false, error: `No melody note with id "${noteId}".` };
    }
    useStore.getState().scoreDeleteNote(noteId);
    if (useStore.getState().score === beforeScore) {
      return { ok: false, error: 'That event cannot be deleted without rewriting its tuplet.' };
    }
    return { ok: true, message: 'Deleted the note.' };
  },
});

const scoreNudgeNote = defineCommand({
  name: 'score_nudge_note',
  title: 'Nudge a melody note',
  description:
    'Move a melody note by semitones (positive = up), respelling it in the chart key. ' +
    'Rests cannot be nudged.',
  schema: z
    .object({ noteId: z.string().min(1), semitones: z.number().int().min(-24).max(24) })
    .strict(),
  run: ({ noteId, semitones }) => {
    const found = describeEvent(useStore.getState().score, noteId);
    if (!found) return { ok: false, error: `No melody note with id "${noteId}".` };
    if (found.rest) return { ok: false, error: 'That is a rest — nothing to nudge.' };
    useStore.getState().scoreNudgeNote(noteId, semitones);
    return {
      ok: true,
      message: `Nudged the note ${semitones > 0 ? 'up' : 'down'} ${Math.abs(semitones)} semitone(s).`,
    };
  },
});

const scoreRespellNote = defineCommand({
  name: 'score_respell_note',
  title: 'Respell a melody note',
  description:
    'Flip a melody note to another single-accidental spelling of its pitch (C# ↔ Db, E ↔ Fb).',
  schema: z.object({ noteId: z.string().min(1) }).strict(),
  run: ({ noteId }) => {
    const beforeScore = useStore.getState().score;
    const found = describeEvent(beforeScore, noteId);
    if (!found) return { ok: false, error: `No melody note with id "${noteId}".` };
    useStore.getState().scoreRespellNote(noteId);
    if (useStore.getState().score === beforeScore) {
      return { ok: false, error: 'That pitch has no alternate accidental spelling.' };
    }
    return { ok: true, message: 'Respelled the note.' };
  },
});

const scoreClearMelody = defineCommand({
  name: 'score_clear_melody',
  title: 'Clear the melody',
  description: 'Remove the whole melody from the loaded chart. The chords stay. Undoable.',
  schema: z.object({}).strict(),
  run: () => {
    useStore.getState().scoreClearMelody();
    return { ok: true, message: 'Cleared the melody.' };
  },
});

const scoreUndo = defineCommand({
  name: 'score_undo',
  title: 'Undo melody edit',
  description: 'Undo the last melody edit on the Score page (melody-scoped history).',
  schema: z.object({}).strict(),
  run: () => {
    if (useStore.getState().melodyPast.length === 0) {
      return { ok: false, error: 'Nothing to undo.' };
    }
    useStore.getState().scoreUndo();
    return { ok: true, message: 'Undid the last melody edit.' };
  },
});

const scoreRedo = defineCommand({
  name: 'score_redo',
  title: 'Redo melody edit',
  description: 'Redo a melody edit previously undone with score_undo.',
  schema: z.object({}).strict(),
  run: () => {
    if (useStore.getState().melodyFuture.length === 0) {
      return { ok: false, error: 'Nothing to redo.' };
    }
    useStore.getState().scoreRedo();
    return { ok: true, message: 'Redid the melody edit.' };
  },
});

const scoreTidyTranscription = defineCommand({
  name: 'score_tidy_transcription',
  title: 'Tidy an imported transcription',
  description:
    'Clean up an AI-transcribed score (e.g. a muscriptor MusicXML import): detect the key from ' +
    'the content, stamp the signature and respell every note to it, re-group the bars when the ' +
    'harmonic rhythm decisively contradicts the written meter, quantize 64th/128th rhythm ' +
    'dust to a 16th grid, DETECT THE CHORD CHANGES from the accompaniment into the empty ' +
    'harmony lane (authored chords are never overwritten), and stamp lead/comp part roles so ' +
    'the workbench melody carries the head. One undoable edit; steps that cannot apply are ' +
    'reported, never silent.',
  schema: z.object({}).strict(),
  run: () => {
    const report = useStore.getState().tidyScoreTranscription();
    return { ok: true, message: describeTidyReport(report) };
  },
});

export const APP_COMMANDS: ErasedCommand[] = [
  voicingPins,
  tuneTaste,
  goToPage,
  openLibrary,
  loadTune,
  newChart,
  setChartDetails,
  transpose,
  addChord,
  updateChord,
  removeChord,
  replaceChord,
  reharmLayer,
  reharmSuggest,
  reharmApply,
  reharmPassage,
  reharmAudition,
  importIReal,
  importAbc,
  exportIReal,
  saveChart,
  setTempo,
  setPlayback,
  setPractice,
  setBass,
  setComping,
  setMelody,
  setSections,
  setView,
  toggleTheme,
  togglePageView,
  printSheet,
  pausePlayback,
  resumePlayback,
  stopPlayback,
  createPlaylist,
  scoreAddNote,
  scoreAppendNote,
  scoreUpdateNote,
  scoreDeleteNote,
  scoreNudgeNote,
  scoreRespellNote,
  scoreClearMelody,
  scoreUndo,
  scoreRedo,
  scoreTidyTranscription,
];

let registered = false;
/** Idempotent — safe to call from multiple entry points (palette, tests, chat). */
export function ensureAppCommands(): void {
  if (registered) return;
  registerCommands(APP_COMMANDS);
  registered = true;
}
